import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataTableDirective } from 'angular-datatables';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CreateResult } from '../../models/create-result';
import { DataTablesResponseViewModel } from '../../models/data-tables-response-view-model';
import { TerminalSignUpViewModel } from '../../models/terminal-signup-view-model';
import { TerminalTrackingViewModel } from '../../models/terminal-tracking-view-model';
import { TrackingInformationViewModel } from '../../models/tracking-information-view-model';
import { WaybillResponseViewModel } from '../../models/waybill-response-view-model';
import { AuthenticationService } from '../../services/authentication.service';
import { PermissionEnum } from '../../services/guards/permissions';
import { TerminalService } from '../../services/terminal.service';
import { UserDataStoreService } from '../../services/user-data-store.service';
import { AddUpdateTerminalLogViewModel } from '../../models/add-update-terminal-log-view-model';
import { ResultMessageViewModel } from '../../models/result-message-view-model';


@Component({
  selector: 'app-dispatch-terminal',
  templateUrl: './dispatch-terminal.component.html',
  styleUrls: ['./dispatch-terminal.component.scss']
})
export class DispatchTerminalComponent implements OnInit {

  public message: string;
  public success: boolean;
  public raiseDanger: boolean;
  public danger: string;
  public canManage: boolean;
  public canEditMerchantSignUp: boolean;
  public TerminalFormGroup: FormGroup;
  public createResult: CreateResult;
  public dispatchedTerminals: TerminalSignUpViewModel[];
  public transactions: TrackingInformationViewModel[];
  public serialNumber: string;
  public merchantIdSearch = '';
  public terminalIdSearch = '';
  public waybillSearch = '';
  public baseSerialNumberSearch = '';
  public serialNumberSearch = '';
  public simNumberSearch = '';
  public inUseDateSearch = '';
  public terminals: TerminalSignUpViewModel[] = [];
  //public type: string = '';
  public terminal: TerminalSignUpViewModel;
  //public resultMessage: ResultMessageViewModel;

  private _destroyed$ = new Subject<any>();
  public dtTrigger: Subject<boolean> = new Subject();
  public searchDebounce = new Subject<string>();

  @ViewChild(DataTableDirective)
  public dtElement: DataTableDirective;

  public nameSearch = '';
  public statusSearch = '';
  public terminalLog: AddUpdateTerminalLogViewModel;

  constructor(
    private _httpClient: HttpClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _userDataStore: UserDataStoreService,
    private _authenticationService: AuthenticationService,
    private _modalService: NgbModal,
    private _ngxUiLoaderService: NgxUiLoaderService,
    private _toastService: ToastrService,
    private _formBuilder: FormBuilder,
    private _terminalService: TerminalService) {
    this._route.params.pipe(takeUntil(this._destroyed$)).subscribe(async params => {

      this.canManage = this._userDataStore.hasRequiredPermissions([PermissionEnum.ViewTerminalDispatched]);
      if (!this.canManage) {
        this._toastService.warning("You do not have the required permissions to view this page, Please contact your system administrator", "Warning");
        this._router.navigate(['/']);
      }

    });

    this.searchDebounce.pipe(
      debounceTime(800),
      distinctUntilChanged())
      .subscribe(async value => {
        await this.refreshTable();
      });


  }


  async ngOnInit() {

    this.dispatchedTerminals = await this._terminalService.GetDispatchedTerminal();
  }

  public async ngAfterViewInit(): Promise<void> {
    this.dtTrigger.next();
    // await this.init();
  }

  private resetDatatable(): void {

    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next();
    });

  }

  private async refreshTable(): Promise<void> {
    await this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      this.setupFilters();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next();
    });
  }

  public setupFilters(): void {
    const searchColumns = this.columns.map((col) => {
      if (!!(col).search) {
        return { search: (col).search.value, regex: (col).search.regex };
      } else {
        return null;
      }
    });
    //this.dtOptions.searchCols = searchColumns;
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      let i = 0;
      for (const item of searchColumns) {
        if (item) {
          dtInstance.column(i).search(item.search, item.regex);
        }
        i++;
      }
    })
  }

  public columns: any[] = [
    { data: 'terminalid', title: 'Terminal Id', visible: true, searchable: true, search: { value: this.terminalIdSearch, regex: true } },
    { data: 'wayBill', title: 'Waybill', visible: true, searchable: true, search: { value: this.waybillSearch, regex: true } },
    { data: 'serialNumber', title: 'Serial number', visible: true, searchable: true, search: { value: this.serialNumberSearch, regex: true } },
    { data: 'baseSerialNumber', title: 'Base serial number', visible: true, searchable: true, search: { value: this.baseSerialNumberSearch, regex: true } },
    { data: 'simNumber', title: 'Sim number', visible: true, searchable: true, search: { value: this.simNumberSearch, regex: true } },
    { data: 'merchantNumber', title: 'Merchant number', visible: true, searchable: true, search: { value: this.merchantIdSearch, regex: true } },
    { data: 'inUseDate', title: 'Dispatched date', visible: true, searchable: true, search: { value: this.inUseDateSearch, regex: true } },
  ];

  private searchColumns = this.columns.map((col) => {
    if (!!col.search) {
      return { search: col.search.value, regex: col.search.regex };
    } else {
      return null;
    }
  });

  public dtOptions: DataTables.Settings = {
    pageLength: 10,
    serverSide: true,
    processing: true,
    ajax: (dataTablesParameters: any, callback) => {
      this._httpClient
        .post<DataTablesResponseViewModel>(
          `${environment.apiUrl}/api/terminal/GetDispatchedTerminals`,
          dataTablesParameters, {}
        ).subscribe(resp => {

          this._ngxUiLoaderService.start();
          this.dispatchedTerminals = resp.data;
          this._ngxUiLoaderService.stop();

          callback({
            recordsTotal: resp.recordsTotal,
            recordsFiltered: resp.recordsFiltered,
            data: []
          });
        });
    },
    columns: this.columns,
    searchCols: this.searchColumns
  };

  public get displayColumns() {
    return this.dtOptions.columns.filter(x => x.visible != false);
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  public async submit(): Promise<void> {
    if (!this.serialNumber) {
      this.raiseDanger = true;
      this.danger = 'Serial Number/Waybill number is required!';
      this.success = false;
      return;
    }
    debugger;
    this._ngxUiLoaderService.start();

      const terminalType = await this._terminalService.GetTerminalSignedUpType(this.serialNumber);
      const type = terminalType.message;

      let dispatchResult: any;
      let terminals: any[];

      if (type !== 'bulk') {
        const terminal = await this._terminalService.GetTerminalSignedUpByMerchant(this.serialNumber);

        if (!terminal) {
          this._toastService.error('No terminal found for the given serial number.', 'Error');
          this.raiseDanger = true;
          this.danger = 'No terminal found.';
          this.success = false;
          this._ngxUiLoaderService.stop();
          return;
        }

        dispatchResult = await this._terminalService.DispatchTerminal(this.serialNumber);
        terminals = [terminal];
      } else {
        dispatchResult = await this._terminalService.DispatchTerminal(this.serialNumber);
        terminals = await this._terminalService.GetBulkTerminalSignedUpByMerchant(this.serialNumber);
      }

      if (dispatchResult.success) {
        this._toastService.success(type !== 'bulk' ? dispatchResult.message : 'Terminals dispatched successfully.', 'Success');
        this.raiseDanger = false;

        //for (const term of terminals) {
        //  const terminalLog = {
        //    CustodianshipId: 'Zoho',
        //    EventId: 3,
        //    MerchantNumber: term.merchantNumber,
        //    TerminalId: term.terminalId,
        //    SerialNumber: term.serialNumber,
        //  };

        //  await this._terminalService.LogTerminalInfor(terminalLog);
        //}

        this.dispatchedTerminals = await this._terminalService.GetDispatchedTerminal();
      } else {
        this._toastService.error(dispatchResult.message, 'Error');
        this.raiseDanger = true;
        this.danger = dispatchResult.message;
        this.success = false;
      }
  
      this._ngxUiLoaderService.stop();
  }
}
